/**
 * PRODUCT CATEGORIES — the coarse list, one per kind of thing a company sells.
 *
 * ⚠️ THE DEFINITION MOVED TO `@repo/types` ON 2026-09-19 (VRG-610). This file is a
 * re-export so its ~20 call sites and the `constants/categories` import path are
 * untouched, and so there is still exactly ONE definition. Edit
 * `packages/types/src/product/product-categories.ts`, which also carries the product
 * type -> category map and the module-load guard over both.
 *
 * It moved because `apps/api` needs the list: `CategoryMapping.veraCategory` in
 * `apps/api/src/utils/category-mapper.ts` was typed `string`, so a category the
 * platform cannot display compiled clean and was auto-assigned to imported products.
 * `apps/api` cannot import `apps/platform`, so a closed set had to live in a package.
 * Same argument, same shape, as VRG-573 hoisting `ALL_PRODUCT_TYPES`.
 *
 * Renamed from CATEGORY_TYPES on 2026-09-18 (Spencer). "Category type" said the same
 * word twice and did not distinguish this from the finer list; these are product
 * CATEGORIES, and ALL_PRODUCT_TYPES in @repo/types holds product TYPES.
 *
 * ── ⚠️ THE TWO LISTS ARE DIFFERENT GRAINS, NOT TWO SPELLINGS ─────────────
 *   PRODUCT_CATEGORIES (23)  what a COMPANY sells. Stored on companies.categories.
 *   ALL_PRODUCT_TYPES      (68)  what a TAG is best for. Drives best_for_product_types and search.
 *
 * ── STATUS OF THE apps/api/src/utils/category-mapper.ts GAP ──────────────
 * This file used to warn that `cannabis`, `legal_documents`, `pharmaceuticals` and
 * `supplements` had no mapper entry, so a product that should land in one of them
 * landed wherever the nearest prefix match put it, silently.
 *
 * VRG-610 closed THREE of them against real Shopify Standard Product Taxonomy codes
 * (`cannabis` fb-4, `pharmaceuticals` hb-1-16, `supplements` hb-1-9-6). TWO remain
 * open ON PURPOSE, because the Shopify taxonomy has no node for them and a wrong code
 * mis-files silently where an absent one merely does nothing:
 *
 *   · `legal_documents`  — no taxonomy node exists.
 *   · `membership_cards` — no taxonomy node exists; the reference CSV put it on the
 *                          same `ae` code as `event_tickets`, and two categories
 *                          cannot share a code under a longest-prefix matcher.
 *
 * The mapper states this in full at `CATEGORIES_WITHOUT_A_SHOPIFY_CODE`.
 */
export {
  PRODUCT_CATEGORIES,
  CATEGORY_DISPLAY_NAMES,
  PRODUCT_TYPE_CATEGORIES,
  categoriesForProductType,
  categoriesForProductTypes,
} from '@repo/types';
export type { ProductCategory } from '@repo/types';

import { PRODUCT_CATEGORIES as CATEGORIES } from '@repo/types';
import type { ProductCategory } from '@repo/types';

/** @deprecated Use PRODUCT_CATEGORIES / ProductCategory. Kept so the rename
 *  lands in one pass without touching every call site at once. */
export const CATEGORY_TYPES = CATEGORIES;
/** @deprecated Use ProductCategory. */
export type CategoryType = ProductCategory;
